import * as zod from 'zod';

/**
 * The bot's reservation lock — at most one row in postgres-bot's
 * `control_lock` table, owned by the feathers-bot `controlLock` service.
 * The row is the bot's read-only source of truth: external callers can only
 * read it; writes arrive exclusively over loopback (the cloud phone-home
 * sync). An expired lock is treated as absent — the lock self-frees at
 * `expiresAt` with no background job — and no row at all means "free".
 */

export const ControlLockHolderType = zod.enum(['service', 'user']);
export type ControlLockHolderType = zod.infer<typeof ControlLockHolderType>;

// Timestamps are ISO strings: pg returns timestamps as strings on the bot.
export const ControlLockData = zod.object({
  id: zod.string(),
  holderType: ControlLockHolderType.nullable(),
  holderId: zod.string().nullable(), // user email or service-supplied uuid
  holderDisplay: zod.string().nullable(),
  reason: zod.string().nullable(),
  acquiredAt: zod.string().nullable(),
  expiresAt: zod.string().nullable(),
  // The cloud reservation_request that produced the current lock state — the
  // phone-home sync's idempotency/confirmation key; null when free.
  grantedRequestId: zod.string().nullable(),
  createdAt: zod.string(),
  updatedAt: zod.string(),
});
export type ControlLockData = zod.infer<typeof ControlLockData>;

/**
 * Write allowlist for lock patches. `id`/`createdAt` are server-owned.
 * `updatedAt` must stay IN this schema: the timestamp hook runs before
 * `parse`, which would otherwise strip the value the hook just set.
 */
export const ControlLockPatchInput = ControlLockData.omit({
  id: true,
  createdAt: true,
});
export type ControlLockPatchInput = zod.infer<typeof ControlLockPatchInput>;

/** A lock is live iff it has a holder and has not expired. Expired = absent. */
export function isControlLockLive(
  lock: Pick<ControlLockData, 'holderId' | 'expiresAt'> | null | undefined,
  now = new Date(),
): boolean {
  return (
    lock?.holderId != null &&
    lock.expiresAt != null &&
    new Date(lock.expiresAt) > now
  );
}
